import React, { useEffect, useRef, useState } from 'react'
import { useIntl } from 'react-intl'
import { Box, Checkbox, MenuItem, Select, TextField } from '@mui/material'
import { ui } from '../../../utils/uiTokens'
import { CrmIntakeView, CrmObStatus, CrmOnbStage, crmApi } from '../../../features/crm/crmApi'
import { ErrorText, FieldLabel, Hint, OutlineButton, Pill, TONES } from '../shared/crmUi'
import { downscaleImage, formatPhone, genPassword } from '../shared/format'

// Сервис бөлімі: 8 этапов подключения ученика к курсу. После 4-го этапа заявку можно утверждать, 5-8 открываются после утверждения.
export const APPROVE_AFTER = 4

interface Def {
    items: number
    statuses: CrmObStatus[]
    ok: CrmObStatus
    bad?: CrmObStatus
    half?: CrmObStatus
    link?: boolean
    login?: boolean
    warn?: boolean
}

const DEFS: Def[] = [
    { items: 3, statuses: ['PENDING', 'DONE'], ok: 'DONE', link: true },
    { items: 2, statuses: ['PENDING', 'SIGNED', 'NOT_SIGNED'], ok: 'SIGNED', bad: 'NOT_SIGNED', warn: true },
    { items: 3, statuses: ['PENDING', 'DONE'], ok: 'DONE' },
    { items: 6, statuses: ['PENDING', 'DONE', 'PROBLEM'], ok: 'DONE', bad: 'PROBLEM', login: true },
    { items: 4, statuses: ['PENDING', 'DONE', 'PROBLEM'], ok: 'DONE', bad: 'PROBLEM' },
    { items: 7, statuses: ['PENDING', 'PARTIAL', 'DONE'], ok: 'DONE', half: 'PARTIAL' },
    { items: 4, statuses: ['PENDING', 'DONE'], ok: 'DONE' },
    { items: 3, statuses: ['PENDING', 'DONE'], ok: 'DONE' },
]

const stageOf = (x: CrmIntakeView, i: number): CrmOnbStage => {
    const s = x.onboarding?.stages?.find((g) => g.stageNo === i + 1)
    const checks = Array.from({ length: DEFS[i].items }, (_, j) => !!s?.checks?.[j])
    return { stageNo: i + 1, status: s?.status || 'PENDING', checks, photos: s?.photos ?? [], doneAt: s?.doneAt ?? null }
}
const isFull = (g: CrmOnbStage) => g.checks.every(Boolean) && g.photos.length > 0
const isDone = (g: CrmOnbStage, d: Def) => isFull(g) && (g.status === d.ok || (!!d.half && g.status === d.half))

// сколько этапов выполнено (для «N/8» в списке и проверки утверждения)
export const obDoneList = (x: CrmIntakeView) => DEFS.map((d, i) => isDone(stageOf(x, i), d))
export const obStagesReady = (x: CrmIntakeView, n = APPROVE_AFTER) => obDoneList(x).slice(0, n).every(Boolean)

const passKey = (id: number) => `crmPass:${id}`
export const useIntakePassword = (id: number) => {
    const [pass, setPass] = useState('')
    useEffect(() => {
        try {
            setPass(sessionStorage.getItem(passKey(id)) || '')
        } catch {
            setPass('')
        }
    }, [id])
    const set = (v: string) => {
        setPass(v)
        try {
            sessionStorage.setItem(passKey(id), v)
        } catch {
            /* sessionStorage недоступен */
        }
    }
    return [pass, set] as const
}

const Onboarding: React.FC<{
    intake: CrmIntakeView
    password: string
    onPassword: (v: string) => void
    onZoom: (urls: string[]) => void
}> = ({ intake: x, password, onPassword, onZoom }) => {
    const { formatMessage } = useIntl()
    const [saveStage] = crmApi.useSaveOnboardingStageMutation()
    const [delPhoto] = crmApi.useDeleteOnboardingPhotoMutation()
    const [saveMeta] = crmApi.useSaveOnboardingMetaMutation()
    const approved = x.status === 'APPROVED'
    const stages = DEFS.map((d, i) => stageOf(x, i))
    const done = stages.map((g, i) => isDone(g, DEFS[i]))
    const doneN = done.filter(Boolean).length
    const open = (i: number) => i === 0 || (done[i - 1] && (i < APPROVE_AFTER || approved))
    const [busy, setBusy] = useState(false)
    const [contract, setContract] = useState(x.onboarding?.contractUrl || '')
    const [contractErr, setContractErr] = useState('')
    const [note, setNote] = useState(x.onboarding?.finalNote || '')
    const loginIssued = !!password || !!x.onboarding?.loginIssuedAt || approved
    // куда вставлять Ctrl+V: последняя зона под курсором/фокусом, иначе последний открытый этап
    const pasteTo = useRef<number | null>(null)
    const addRef = useRef<(i: number, files: File[]) => void>(() => undefined)

    useEffect(() => setContract(x.onboarding?.contractUrl || ''), [x.onboarding?.contractUrl])
    useEffect(() => setNote(x.onboarding?.finalNote || ''), [x.onboarding?.finalNote])

    const run = async (fn: () => Promise<unknown>) => {
        setBusy(true)
        try {
            await fn()
        } catch {
            /* общий обработчик ошибок */
        } finally {
            setBusy(false)
        }
    }

    const send = (i: number, checks: boolean[], status: CrmObStatus, files: File[] = []) => {
        const body = new FormData()
        checks.forEach((c) => body.append('checks', String(c)))
        body.append('status', status)
        files.forEach((f) => body.append('files', f))
        return saveStage({ id: x.id, stageNo: i + 1, body }).unwrap()
    }

    const issueLogin = (pass: string) => {
        onPassword(pass)
        return saveMeta({ id: x.id, issueLogin: true }).unwrap()
    }

    const toggleCheck = (i: number, j: number, on: boolean) => {
        const g = stages[i]
        const checks = g.checks.map((c, k) => (k === j ? on : c))
        const full = checks.every(Boolean) && g.photos.length > 0
        const status = !full && (g.status === DEFS[i].ok || g.status === DEFS[i].half) ? 'PENDING' : g.status
        run(() => send(i, checks, status))
    }

    const addFiles = (i: number, list: File[]) => {
        const images = list.filter((f) => f.type.startsWith('image/'))
        if (!images.length || busy || !open(i)) return
        run(async () => {
            const small = await Promise.all(images.map((f) => downscaleImage(f)))
            // этап 4: логин-пароль ещё не открыт - открываем автоматически
            if (DEFS[i].login && !loginIssued) await issueLogin(genPassword())
            await send(i, stages[i].checks.map(() => true), DEFS[i].ok, small)
        })
    }
    addRef.current = addFiles

    const removePhoto = (i: number, photoId: number) =>
        run(async () => {
            const g = stages[i]
            await delPhoto(photoId).unwrap()
            // скриншотов не осталось - статус возвращается в «Күтілуде»
            if (g.photos.length <= 1 && (g.status === DEFS[i].ok || g.status === DEFS[i].half)) await send(i, g.checks, 'PENDING')
        })

    useEffect(() => {
        const onPaste = (e: ClipboardEvent) => {
            const files = Array.from(e.clipboardData?.files ?? [])
            if (!files.length) return
            let i = pasteTo.current
            if (i == null || !open(i)) {
                const opens = DEFS.map((_, k) => k).filter((k) => open(k))
                i = opens.length ? opens[opens.length - 1] : null
            }
            if (i == null) return
            e.preventDefault()
            addRef.current(i, files)
        }
        document.addEventListener('paste', onPaste)
        return () => document.removeEventListener('paste', onPaste)
    })

    const saveContract = () => {
        const v = contract.trim()
        if (v && !/^https?:\/\//i.test(v)) return setContractErr(formatMessage({ id: 'crmUrlInvalid' }))
        setContractErr('')
        if (v !== (x.onboarding?.contractUrl || '')) run(() => saveMeta({ id: x.id, contractUrl: v }).unwrap())
    }

    const final = x.onboarding?.finalStatus || ''

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                <Box sx={{ fontWeight: 700, fontSize: '16px', color: ui.ink }}>{formatMessage({ id: 'crmObTitle' })}</Box>
                <Pill tone={doneN === 8 ? 'ok' : 'grey'}>{formatMessage({ id: 'crmObProgress' }, { n: doneN })}</Pill>
            </Box>
            {DEFS.map((d, i) => {
                const g = stages[i]
                const isOpen = open(i)
                const dn = done[i]
                const bad = !!d.bad && g.status === d.bad
                const full = isFull(g)
                const tn = dn ? (g.status === d.half ? TONES.wait : TONES.ok) : bad ? TONES.bad : null
                const no = i + 1
                return (
                    <React.Fragment key={no}>
                        {i === APPROVE_AFTER && !approved && done[i - 1] && (
                            <Box sx={{ background: TONES.blue.bg, color: TONES.blue.text, borderRadius: '12px', padding: '10px 14px', fontWeight: 600, fontSize: '13px' }}>
                                {formatMessage({ id: 'crmObGate' })}
                            </Box>
                        )}
                        <Box
                            component="section"
                            sx={{
                                border: `1px solid ${dn ? TONES.ok.border : bad ? TONES.bad.text : ui.border}`,
                                borderRadius: '14px',
                                background: dn ? TONES.ok.bg : ui.surface,
                                overflow: 'hidden',
                                opacity: isOpen ? 1 : 0.55,
                            }}
                        >
                            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '32px minmax(0, 1fr)', sm: '32px minmax(0, 1fr) auto' }, gap: '12px', alignItems: 'center', padding: '12px 14px' }}>
                                <Box
                                    sx={{
                                        width: 32,
                                        height: 32,
                                        borderRadius: '10px',
                                        display: 'grid',
                                        placeItems: 'center',
                                        fontWeight: 800,
                                        background: dn ? TONES.ok.text : TONES.grey.bg,
                                        color: dn ? '#fff' : ui.muted,
                                    }}
                                >
                                    {dn ? '✓' : no}
                                </Box>
                                <Box sx={{ minWidth: 0 }}>
                                    <Box sx={{ fontSize: '11px', fontWeight: 800, letterSpacing: '0.06em', color: ui.muted }}>{formatMessage({ id: 'crmObStageLabel' }, { n: no })}</Box>
                                    <Box sx={{ fontWeight: 700, fontSize: '14px' }}>{formatMessage({ id: `crmObT_${no}` })}</Box>
                                </Box>
                                <Box sx={{ display: 'flex', gap: '8px', alignItems: 'center', gridColumn: { xs: '1 / -1', sm: 'auto' }, fontSize: '12px', color: ui.muted }}>
                                    {formatMessage({ id: 'crmObStatus' })}
                                    <Select
                                        size="small"
                                        value={g.status}
                                        disabled={!isOpen || busy}
                                        onChange={(e) => run(() => send(i, g.checks, e.target.value as CrmObStatus))}
                                        sx={{ fontSize: '13px', minWidth: 150 }}
                                    >
                                        {d.statuses.map((v) => (
                                            <MenuItem key={v} value={v} disabled={(v === d.ok || v === d.half) && !full}>
                                                {formatMessage({ id: `crmObSt_${v}` })}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                    <Box component="span" sx={{ width: 10, height: 10, borderRadius: '50%', background: tn ? tn.text : ui.border, display: { xs: 'none', sm: 'block' } }} />
                                </Box>
                            </Box>
                            {isOpen ? (
                                <Box sx={{ padding: { xs: '0 14px 14px', sm: '0 14px 14px 58px' } }}>
                                    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                                        {Array.from({ length: d.items }, (_, j) => (
                                            <Box
                                                key={j}
                                                component="label"
                                                sx={{ display: 'flex', alignItems: 'flex-start', cursor: 'pointer', fontSize: '13.5px', color: g.checks[j] ? TONES.ok.text : ui.ink }}
                                            >
                                                <Checkbox
                                                    size="small"
                                                    sx={{ padding: '4px 8px 4px 0' }}
                                                    checked={g.checks[j]}
                                                    disabled={busy || (!!d.login && j < 4 && !loginIssued)}
                                                    onChange={(e) => toggleCheck(i, j, e.target.checked)}
                                                />
                                                <Box component="span" sx={{ paddingTop: '4px' }}>{formatMessage({ id: `crmObI_${no}_${j + 1}` })}</Box>
                                            </Box>
                                        ))}
                                    </Box>
                                    {d.link && (
                                        <Box sx={{ marginTop: '8px' }}>
                                            <FieldLabel required>{formatMessage({ id: 'crmObContract' })}</FieldLabel>
                                            <TextField
                                                size="small"
                                                fullWidth
                                                value={contract}
                                                placeholder="https://…"
                                                inputProps={{ inputMode: 'url' }}
                                                error={!!contractErr}
                                                onChange={(e) => setContract(e.target.value)}
                                                onBlur={saveContract}
                                                sx={{ background: ui.card }}
                                            />
                                            <ErrorText text={contractErr} />
                                        </Box>
                                    )}
                                    {d.login && (
                                        <Box sx={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap', margin: '8px 0' }}>
                                            <Box sx={{ border: `1px solid ${ui.border}`, background: ui.card, borderRadius: '10px', padding: '6px 12px', fontSize: '13px', display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
                                                <Box><Box component="span" sx={{ color: ui.muted, marginRight: '6px' }}>{formatMessage({ id: 'crmLogin' })}</Box>{x.login || formatPhone(x.data.studentPhone)}</Box>
                                                {(password || (approved && x.tempPassword)) && (
                                                    <Box sx={{ fontFamily: 'monospace', fontWeight: 700 }}>
                                                        <Box component="span" sx={{ color: ui.muted, marginRight: '6px', fontFamily: 'inherit', fontWeight: 400 }}>{formatMessage({ id: 'crmTempPassword' })}</Box>
                                                        {approved ? x.tempPassword : password}
                                                    </Box>
                                                )}
                                            </Box>
                                            {!approved && (
                                                <OutlineButton size="small" disabled={busy} onClick={() => run(() => issueLogin(genPassword()))}>
                                                    🔑 {formatMessage({ id: password ? 'crmRegenerate' : 'crmObOpenPass' })}
                                                </OutlineButton>
                                            )}
                                            {!loginIssued && <Hint>{formatMessage({ id: 'crmObPassFirst' })}</Hint>}
                                            {!approved && !password && !!x.onboarding?.loginIssuedAt && <Hint>{formatMessage({ id: 'crmObPassLost' })}</Hint>}
                                        </Box>
                                    )}
                                    <Box sx={{ marginTop: '10px' }}>
                                        <Box sx={{ fontSize: '12px', fontWeight: 700, marginBottom: '6px' }}>
                                            {formatMessage({ id: 'crmObProof' })} <Box component="span" sx={{ color: TONES.bad.text }}>*</Box>{' '}
                                            <Box component="span" sx={{ color: ui.muted, fontWeight: 400 }}>· {formatMessage({ id: 'crmObProofHint' })}</Box>
                                        </Box>
                                        <Box sx={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center' }}>
                                            {g.photos.map((f) => (
                                                <Box key={f.id} sx={{ position: 'relative', display: 'inline-block' }}>
                                                    <img
                                                        src={f.url}
                                                        alt=""
                                                        onClick={() => onZoom(g.photos.map((p) => p.url))}
                                                        style={{ width: 64, height: 64, objectFit: 'cover', borderRadius: 8, cursor: 'zoom-in', border: `1px solid ${ui.border}` }}
                                                    />
                                                    <Box
                                                        component="button"
                                                        type="button"
                                                        aria-label={formatMessage({ id: 'crmObDelete' })}
                                                        disabled={busy}
                                                        onClick={() => removePhoto(i, f.id)}
                                                        sx={{ position: 'absolute', top: '-7px', right: '-7px', width: 20, height: 20, borderRadius: '50%', border: 0, background: TONES.bad.text, color: '#fff', fontSize: '14px', lineHeight: 1, cursor: 'pointer', display: 'grid', placeItems: 'center', padding: 0 }}
                                                    >
                                                        ×
                                                    </Box>
                                                </Box>
                                            ))}
                                            <DropZone
                                                disabled={busy}
                                                hint={formatMessage({ id: 'crmObDropHint' }, { ok: formatMessage({ id: `crmObSt_${d.ok}` }) })}
                                                label={formatMessage({ id: 'crmObAddShot' })}
                                                onFocusZone={() => (pasteTo.current = i)}
                                                onFiles={(files) => addFiles(i, files)}
                                            />
                                        </Box>
                                    </Box>
                                    {d.warn && (
                                        <Box sx={{ marginTop: '8px', background: TONES.wait.bg, color: TONES.wait.text, borderRadius: '10px', padding: '8px 12px', fontSize: '12.5px', fontWeight: 600 }}>
                                            {formatMessage({ id: 'crmObImportant' }, { text: formatMessage({ id: `crmObWarn_${no}` }) })}
                                        </Box>
                                    )}
                                    {!full && <Hint sx={{ marginTop: '6px' }}>{formatMessage({ id: 'crmObNeedFull' }, { ok: formatMessage({ id: `crmObSt_${d.ok}` }) })}</Hint>}
                                </Box>
                            ) : (
                                <Hint sx={{ padding: '0 14px 14px 58px' }}>
                                    {formatMessage({ id: i >= APPROVE_AFTER && !approved ? 'crmObLockedHead' : 'crmObLockedPrev' })}
                                </Hint>
                            )}
                        </Box>
                    </React.Fragment>
                )
            })}
            {approved && (
                <Box component="section" sx={{ border: `1px solid ${ui.border}`, borderRadius: '14px', background: ui.surface, padding: '12px 14px', opacity: done[7] ? 1 : 0.55 }}>
                    <Box sx={{ fontSize: '11px', fontWeight: 800, letterSpacing: '0.06em', color: ui.muted }}>{formatMessage({ id: 'crmObFinalKicker' })}</Box>
                    <Box sx={{ fontWeight: 700, fontSize: '14px', marginBottom: '6px' }}>{formatMessage({ id: 'crmObFinalTitle' })}</Box>
                    {['COMPLETE', 'ATTENTION'].map((v) => (
                        <Box key={v} component="label" sx={{ display: 'flex', alignItems: 'center', fontSize: '13.5px', cursor: done[7] ? 'pointer' : 'default', color: final === v ? TONES.ok.text : ui.ink }}>
                            <input
                                type="radio"
                                name={`obfin${x.id}`}
                                checked={final === v}
                                disabled={!done[7] || busy}
                                onChange={() => run(() => saveMeta({ id: x.id, finalStatus: v, finalNote: note.trim() }).unwrap())}
                                style={{ marginRight: 8 }}
                            />
                            {formatMessage({ id: `crmObFinal_${v}` })}
                        </Box>
                    ))}
                    <Box sx={{ marginTop: '8px' }}>
                        <FieldLabel>{formatMessage({ id: 'crmObNotes' })}</FieldLabel>
                        <TextField
                            size="small"
                            fullWidth
                            multiline
                            minRows={2}
                            value={note}
                            disabled={!done[7]}
                            onChange={(e) => setNote(e.target.value)}
                            onBlur={() => note.trim() !== (x.onboarding?.finalNote || '') && run(() => saveMeta({ id: x.id, finalStatus: final, finalNote: note.trim() }).unwrap())}
                            sx={{ background: ui.card }}
                        />
                    </Box>
                </Box>
            )}
        </Box>
    )
}

const DropZone: React.FC<{ label: string; hint: string; disabled: boolean; onFiles: (f: File[]) => void; onFocusZone: () => void }> = ({
    label,
    hint,
    disabled,
    onFiles,
    onFocusZone,
}) => {
    const [over, setOver] = useState(false)
    return (
        <Box
            component="label"
            tabIndex={0}
            onFocus={onFocusZone}
            onMouseEnter={onFocusZone}
            onDragOver={(e: React.DragEvent) => {
                e.preventDefault()
                setOver(true)
            }}
            onDragLeave={() => setOver(false)}
            onDrop={(e: React.DragEvent) => {
                e.preventDefault()
                setOver(false)
                if (!disabled) onFiles(Array.from(e.dataTransfer.files))
            }}
            onKeyDown={(e: React.KeyboardEvent<HTMLElement>) => {
                if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault()
                    e.currentTarget.querySelector('input')?.click()
                }
            }}
            sx={{
                position: 'relative',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '2px',
                minWidth: 200,
                maxWidth: 280,
                padding: '10px 14px',
                border: `1.5px ${over ? 'solid' : 'dashed'} ${ui.pink}`,
                borderRadius: '12px',
                background: over ? ui.card : ui.pinkSoft,
                color: ui.pink,
                cursor: 'pointer',
                fontSize: '13px',
                textAlign: 'center',
            }}
        >
            <span>
                📷 <b>{label}</b>
            </span>
            <Box component="span" sx={{ fontSize: '11px', color: ui.muted }}>{hint}</Box>
            <input
                hidden
                type="file"
                accept="image/*"
                multiple
                disabled={disabled}
                onChange={(e) => {
                    onFiles(Array.from(e.target.files ?? []))
                    e.target.value = ''
                }}
            />
        </Box>
    )
}

export default Onboarding
